import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { CollectionAlbum } from '../../../models/collections/collection-album.model';
import { ApiCollectionsService } from '../../../services/collections/api/api-collections.service';
import { AlertsService } from '../../../services/alerts.service';
import { ModalsService } from '../../../services/modals.service';
import { LanguagesService } from '../../../services/languages/languages.service';
import { getMissingTranslationsCount } from '../collection-translation-form.model';
import { CollectionsImagesHelper } from '../../../utils/helpers/collections-images.helper';
import { CollectionAlbumAddModalComponent } from './modals/collection-album-add-modal/collection-album-add-modal.component';
import { CollectionAlbumEditModalComponent } from './modals/collection-album-edit-modal/collection-album-edit-modal.component';
import { UserCurrencyType } from '../../../enums/users/user-currency-type.enum';

@Component({
  templateUrl: 'collection-albums.component.html',
  standalone: true,
  imports: [CommonModule, RouterModule, TooltipModule, CollectionAlbumAddModalComponent, CollectionAlbumEditModalComponent]
})
export class CollectionAlbumsComponent implements OnInit {

  @ViewChild('addModal') addModal!: CollectionAlbumAddModalComponent;
  @ViewChild('editModal') editModal!: CollectionAlbumEditModalComponent;
  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  collectionId: number = 0;
  albums: CollectionAlbum[] = [];
  isLoading: boolean = false;
  uploadingImageId: number = 0;
  totalResults: number = 0;
  resultsPerPage: number = 25;
  pageNumber: number = 1;

  private imageUploadTarget: CollectionAlbum | null = null;

  get totalPages(): number {
    return Math.ceil(this.totalResults / this.resultsPerPage);
  }

  getPageNumbers(): (number | null)[] {
    const total = this.totalPages;
    const current = this.pageNumber;
    const visibleSet = new Set<number>();
    for (let i = 1; i <= Math.min(2, total); i++) visibleSet.add(i);
    for (let i = Math.max(total - 1, 1); i <= total; i++) visibleSet.add(i);
    for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) visibleSet.add(i);
    const sorted = Array.from(visibleSet).sort((a, b) => a - b);
    const result: (number | null)[] = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
      result.push(sorted[i]);
    }
    return result;
  }

  constructor(
    private route: ActivatedRoute,
    private apiCollectionsService: ApiCollectionsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService,
    private languagesService: LanguagesService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const id: string = params['id'];
      if (id) {
        this.collectionId = Number(id);
        this.pageNumber = 1;
        this.refreshAlbums();
      }
    });
  }

  onClickPaginationPrevious(): void {
    this.pageNumber--;
    this.refreshAlbums();
  }

  onClickPaginationNext(): void {
    this.pageNumber++;
    this.refreshAlbums();
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.refreshAlbums();
  }

  getMissingTranslationsCount(album: CollectionAlbum): number {
    return getMissingTranslationsCount(this.languagesService.languages, album.translations);
  }

  hasImage(album: CollectionAlbum): boolean {
    return CollectionsImagesHelper.hasImage(album.imageUpdatedAt);
  }

  getImageUrl(album: CollectionAlbum): string {
    return CollectionsImagesHelper.getImageUrl('album', album.albumId, album.imageUpdatedAt);
  }

  onClickUploadImage(album: CollectionAlbum): void {
    this.imageUploadTarget = album;
    this.imageInput.nativeElement.click();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const album = this.imageUploadTarget;
    input.value = '';
    this.imageUploadTarget = null;
    if (!file || !album) return;
    const fileError = CollectionsImagesHelper.getImageFileError(file);
    if (fileError) {
      this.alertsService.displayAlertError(fileError);
      return;
    }
    this.uploadingImageId = album.albumId;
    this.apiCollectionsService.uploadCollectionAlbumImage(album.albumId, file).then(
      () => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertSuccess('Image uploaded !');
        this.refreshAlbums();
      }, error => {
        this.uploadingImageId = 0;
        this.alertsService.displayAlertError('Error uploading image : ' + error);
      }
    );
  }

  onClickAdd(): void {
    this.addModal.show(() => this.refreshAlbums());
  }

  onClickEdit(album: CollectionAlbum): void {
    this.editModal.show(album, () => this.refreshAlbums());
  }

  onClickDelete(album: CollectionAlbum): void {
    this.modalsService.showConfirmModal(() => {
      this.apiCollectionsService.deleteCollectionAlbum(album.albumId).then(
        () => {
          this.alertsService.displayAlertSuccess('Album deleted !');
          this.refreshAlbums();
        }, error => {
          this.alertsService.displayAlertError('Error deleting album : ' + error);
        }
      );
    });
  }

  private refreshAlbums(): void {
    this.isLoading = true;
    this.apiCollectionsService.getCollectionAlbums(this.collectionId, this.pageNumber, this.resultsPerPage).then(
      response => {
        this.isLoading = false;
        this.albums = response.results;
        this.totalResults = response.resultsTotal;
      }, error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading albums : ' + error);
      }
    );
  }

  getCurrencyTypeName(type: number): string { return UserCurrencyType[type] ?? String(type); }
}
